import { useCallback } from 'react';
import { Row } from '@tanstack/react-table';

import { isPlaceholderTransportUnit } from 'support/helpers/packingList/defaultTransportUnit';
import { WebEDIRowData } from 'support/types/webediTable';

/**
 * Display-only row filtering for the packing-list feature. When active, the transport-units table
 * hides the default placeholder transport unit (no SSCC), so the user only sees the real units they
 * built via the packing-list modal. Line items in a placeholder unit remain visible; their status
 * is derived from placement and shipped quantity by useLineItemsTransportUnitsConfig.
 *
 * The filtering happens on the materialized DataTable rows (see DataTable's `hideRow`), so the
 * underlying form data — and every row's flat index / write-back path — is untouched. The
 * placeholder and its items stay in the form and are stripped only at validate/submit.
 *
 * Once the modal generates SSCCs on apply, the predicate stops matching real units on its own, and
 * a document that arrived with real, SSCC-bearing units has no placeholder rows to hide.
 */
export const usePackingListRowFilter = (packingListEnabled = false) => {
  const hideTransportUnitRow = useCallback(
    (row: Row<WebEDIRowData>) => packingListEnabled && isPlaceholderTransportUnit(row.original),
    [packingListEnabled],
  );

  return {
    hideTransportUnitRow: packingListEnabled ? hideTransportUnitRow : undefined,
  };
};
